import { sortBy } from 'lodash';
import { runInAction } from 'mobx';
import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { useProStudioCtxSelector } from '../xstate/app.xstate';
import { tableStore, type NodeColumns } from '../stores/table-store';
import { assignViewerColorIndices } from '../utils/get-viewer-color-index';
import { getPresenceDisplayName } from './use-row-coworker-focus';
import { useSolutionPresences } from './use-solution-presences';

/** Diff-write a plain Map into an observable map — only notifies changed keys. */
function syncObservableMap<V>(
  target: {
    keys(): IterableIterator<string>;
    get(k: string): V | undefined;
    set(k: string, v: V): void;
    delete(k: string): void;
  },
  next: Map<string, V>
) {
  for (const key of [...target.keys()]) {
    if (!next.has(key)) target.delete(key);
  }
  for (const [key, value] of next) {
    if (target.get(key) !== value) target.set(key, value);
  }
}

export function usePresenceMobXBridge() {
  const { pathname } = useLocation();
  // PSTUDIO-4451: the document's collaborators under live editing.
  const solutionUserPresences = useSolutionPresences();
  const userInfo = useProStudioCtxSelector((ctx) => ctx.userInfo);
  const projectUsers = useProStudioCtxSelector((ctx) => ctx.projectUsers);
  const currentUserId = String(userInfo?.id ?? '');

  useEffect(() => {
    const newMap = new Map<string, { label: string; colorIndex: number }>();

    // Sort active non-current users the same way as viewingListForCurrentPage
    // so colorIndex is consistent between avatar rings and cell borders.
    const activeOthers = sortBy(
      (solutionUserPresences ?? []).filter(
        (p) =>
          p.isActive && String(p.userId ?? p.user?.id ?? '') !== currentUserId
      ),
      (p) => {
        const first = p.user?.firstName ?? '';
        const last = p.user?.lastName ?? '';
        const name = [first, last].filter(Boolean).join(' ').toLowerCase();
        return name || String(p.userId ?? p.id ?? '');
      }
    );

    const colorIndexMap = assignViewerColorIndices(activeOthers);

    activeOthers
      .filter((p) => p.pagePath === pathname && p.pageParams?.focusedCell)
      .forEach((p) => {
        const fc = p.pageParams!.focusedCell!;
        const cellId = `${fc.rowId}-${fc.columnId}`;
        const name = getPresenceDisplayName(p, projectUsers);
        const userId = String(p.userId ?? p.user?.id ?? '');
        const colorIndex = colorIndexMap.get(userId) ?? 0;
        const existing = newMap.get(cellId);
        newMap.set(cellId, {
          label: existing ? `${existing.label}, ${name}` : name,
          colorIndex: existing?.colorIndex ?? colorIndex,
        });
      });

    // PSTUDIO-2372: expand each coworker's selection range. The cell range
    // travels as bounding corners only — re-expand the rectangle locally via
    // getCellsBetween (visual column order); rows travel as exact rowIds.
    // First coworker to claim a cell/row wins the tint (ranges rarely overlap
    // and the anchor label already disambiguates).
    const newRangeCells = new Map<string, number>();
    const newRangeRows = new Map<string, number>();
    activeOthers
      .filter((p) => p.pagePath === pathname && p.pageParams?.selection)
      .forEach((p) => {
        const selection = p.pageParams!.selection!;
        const userId = String(p.userId ?? p.user?.id ?? '');
        const colorIndex = colorIndexMap.get(userId) ?? 0;

        if (selection.cellRange) {
          const { anchor, focus } = selection.cellRange;
          const cells = tableStore.getCellsBetween(
            {
              cellId: `${anchor.rowId}-${anchor.columnId}`,
              rowId: anchor.rowId,
              columnId: anchor.columnId as NodeColumns,
            },
            {
              cellId: `${focus.rowId}-${focus.columnId}`,
              rowId: focus.rowId,
              columnId: focus.columnId as NodeColumns,
            }
          );
          for (const cell of cells) {
            if (!newRangeCells.has(cell.cellId)) {
              newRangeCells.set(cell.cellId, colorIndex);
            }
          }
        }

        for (const rowId of selection.rowIds ?? []) {
          if (!newRangeRows.has(rowId)) newRangeRows.set(rowId, colorIndex);
        }
      });

    runInAction(() => {
      // Remove stale keys — only notifies cells that lost focus
      for (const key of tableStore.coworkerFocusByCellId.keys()) {
        if (!newMap.has(key)) tableStore.coworkerFocusByCellId.delete(key);
      }
      // Add/update changed keys — only notifies cells whose focus changed
      for (const [key, value] of newMap) {
        const existing = tableStore.coworkerFocusByCellId.get(key);
        if (
          !existing ||
          existing.label !== value.label ||
          existing.colorIndex !== value.colorIndex
        ) {
          tableStore.coworkerFocusByCellId.set(key, value);
        }
      }
      syncObservableMap(tableStore.coworkerRangeByCellId, newRangeCells);
      syncObservableMap(tableStore.coworkerRangeByRowId, newRangeRows);
    });
  }, [solutionUserPresences, pathname, currentUserId, projectUsers]);
}
